/**
 * Clock + ring motif — the two signature "Presence" decorations.
 *
 * `useNow()` ticks once a second so the giant glanceable clock stays live; it is
 * the only state here and it is purely presentational (wall-clock time, no
 * business logic). `Clock` renders the time / AM-PM / long date / room sub-line;
 * `RingMotif` renders the drifting concentric rings (burn-in-safe).
 */

import { useEffect, useState } from 'react';

/** Re-render every second. */
export function useNow(): Date {
    const [now, setNow] = useState<Date>(() => new Date());

    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);

        return () => clearInterval(id);
    }, []);

    return now;
}

function parts(now: Date): { hm: string; ap: string; date: string } {
    let hours = now.getHours();
    const minutes = now.getMinutes();
    const ap = hours >= 12 ? 'PM' : 'AM';

    hours = hours % 12;
    if (hours === 0) {
        hours = 12;
    }

    const hm = `${hours}:${String(minutes).padStart(2, '0')}`;
    const date = now.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });

    return { hm, ap, date };
}

export function Clock({ now, room, small }: { now: Date; room?: string; small?: boolean }): JSX.Element {
    const { hm, ap, date } = parts(now);

    return (
        <div className = { `clock${small ? ' clock--sm' : ''}` } data-testid = "clock">
            <div className = "clock__time">
                <span>{ hm }</span>
                <span className = "clock__ap">{ ap }</span>
            </div>
            <div className = "clock__date">{ date }</div>
            { room ? <div className = "clock__room">{ room }</div> : null }
        </div>
    );
}

/** Drifting concentric rings (the "Presence" motif). */
export function RingMotif({ className }: { className?: string }): JSX.Element {
    return (
        <div className = { `rings${className ? ` ${className}` : ''}` } aria-hidden = "true">
            <div className = "rings__r rings__r--1" />
            <div className = "rings__r rings__r--2" />
            <div className = "rings__r rings__r--3" />
            <div className = "rings__r rings__r--4" />
            <div className = "rings__core" />
        </div>
    );
}
